import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DEPENDENCY_TYPES, MAX_LAG_DAYS, type DependencyType } from '../../lib/constants'
import type { ScheduleExternalActivity } from '../../types'
import ActivitySearchCombobox from './ActivitySearchCombobox'

interface Props {
  direction: 'pred' | 'succ'
  projectId: string
  scheduleId: string
  scheduleName: string
  activityId: string
  rowNumberById: Map<string, number>
  /** Resolves a row number of THIS schedule to a picker result; null when unknown. */
  resolveRow: (n: number) => ScheduleExternalActivity | null
  onAdd: (activityId: string, target: ScheduleExternalActivity, type: DependencyType, lagDays: number) => Promise<unknown>
}

/**
 * The "add a new predecessor/successor" row (Task 17 fix round 1: split out of
 * `DependenciesSection.tsx` — review finding #8).
 *
 * Rendered with `key={activityId}` at the call site (`DependenciesSection.tsx`) so React remounts
 * it — clearing any staged `target`/`type`/`lag` — whenever the focused activity changes. Review
 * finding #1 (Critical): without this, a target staged while looking at activity A survived a click
 * to a different row B, and clicking **Add** at that point silently linked the stale target to B
 * instead of A. `activityId` is also threaded explicitly into `onAdd` (not only closed over) so the
 * submit handler always links against the activity that was on screen when Add was clicked — belt
 * and suspenders alongside the `key` remount.
 */
const AddLinkRow: React.FC<Props> = ({ direction, projectId, scheduleId, scheduleName, activityId, rowNumberById, resolveRow, onAdd }) => {
  const { t } = useTranslation()
  const [target, setTarget] = useState<ScheduleExternalActivity | null>(null)
  const [type, setType] = useState<DependencyType>('FS')
  const [lag, setLag] = useState('0')
  const [lagError, setLagError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submitWith = async (activity: ScheduleExternalActivity) => {
    // Stage the target first (review fix, Task 1 Important #1): the Enter-on-row-number path arrives
    // here without a staged `target`, and the chip must show during the request; on failure the
    // target stays staged so one more click on Add retries — same as the click-driven flow.
    setTarget(activity)
    const n = Number(lag)
    if (!Number.isInteger(n) || Math.abs(n) > MAX_LAG_DAYS) { setLagError(t('Schedule_lag_invalid')); return }
    setLagError(null)
    setSubmitting(true)
    try {
      await onAdd(activityId, activity, type, n)
      setTarget(null)
      setLag('0')
      setType('FS')
    } catch {
      /* GanttOps.run() already toasted the error */
    } finally {
      setSubmitting(false)
    }
  }

  // Local peer label is `rowNumber name` (spec §9.7), same as `EdgeRow`; a foreign target has no row
  // number in *this* schedule's tree, so it keeps `scheduleName › name`.
  const targetLabel = target
    ? (target.scheduleId === scheduleId ? `${rowNumberById.get(target.id) ?? target.wbsCode} ${target.name}` : `${target.scheduleName} › ${target.name}`)
    : ''

  return (
    <div>
      <div className="flex items-center gap-1 pt-1">
        <div className="flex-1 min-w-0">
          {target ? (
            <div className="flex items-center gap-1 h-8 px-2 rounded-md border bg-muted text-sm">
              <span className="flex-1 min-w-0 truncate">{targetLabel}</span>
              <button type="button" onClick={() => setTarget(null)} disabled={submitting} aria-label={t('Common_clear')}>
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          ) : (
            <ActivitySearchCombobox
              projectId={projectId} scheduleId={scheduleId} scheduleName={scheduleName}
              excludeActivityId={activityId} rowNumberById={rowNumberById} localByRowNumber={resolveRow}
              disabled={submitting} onSelect={setTarget} onSubmit={(a) => submitWith(a)}
            />
          )}
        </div>
        <Select value={type} disabled={submitting} onValueChange={(v) => setType(v as DependencyType)}>
          <SelectTrigger className="h-8 w-[60px] text-xs px-1.5"><SelectValue /></SelectTrigger>
          <SelectContent>
            {DEPENDENCY_TYPES.map((dt) => <SelectItem key={dt} value={dt}>{dt}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          className="h-8 w-[48px] text-xs px-1.5" type="number" value={lag} disabled={submitting} aria-label={t('Schedule_lag')}
          onChange={(e) => { setLag(e.target.value); if (lagError) setLagError(null) }}
        />
        <Button
          size="icon" variant="outline" className="h-8 w-8 shrink-0" disabled={!target || submitting}
          onClick={() => target && submitWith(target)}
          aria-label={t(direction === 'pred' ? 'Schedule_add_predecessor' : 'Schedule_add_successor')}
        >
          {submitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
      {lagError && <p className="text-xs text-destructive">{lagError}</p>}
    </div>
  )
}

export default AddLinkRow
